import { SHOW_DEV_TOOLS } from '../config.js';
import { parseFloorStorage } from '../utils/ui.js';
import { WHITE_ROOM_ENVIRONMENT } from '../utils/constants.js';

/**
 * UserPreferences — the per-browser half of persistence (environment, view toggles, floor crop),
 * kept out of layouts and share links; StateManager saves it via the same chokepoint as the layout.
 */
export const PREFS_KEY = 'threejs_user_prefs';
export const PREFS_VERSION = 1;
// The uploaded white-room floor lives under its own key: it can be a multi-MB data URL.
export const CUSTOM_FLOOR_KEY = 'white_room_custom_floor';

/** @returns {{dataURL: string, realW: number|null, realH: number|null}|null} */
export function readCustomFloor() {
  try {
    const raw = localStorage.getItem(CUSTOM_FLOOR_KEY);
    return raw ? parseFloorStorage(raw) : null;
  } catch {
    return null;
  }
}

export function saveCustomFloor(record) {
  localStorage.setItem(CUSTOM_FLOOR_KEY, JSON.stringify(record));
}

export function clearCustomFloor() {
  try {
    localStorage.removeItem(CUSTOM_FLOOR_KEY);
  } catch {
    // Storage unavailable — nothing was persisted to clear.
  }
}

export class UserPreferences {
  constructor(app) {
    this.app = app;
    // Last blob read(); lets applyRoomBox() re-run after a layout load without a second parse.
    this._stored = null;
  }

  /**
   * Collect the current preference values off the live managers.
   * @returns {Object}
   */
  collect() {
    const prefs = { version: PREFS_VERSION };

    const sceneManager = this.app.getSceneManager();
    if (sceneManager) {
      prefs.environment = sceneManager.getEnvironmentState();
      // Omitted at the device default so an untouched browser re-resolves it each load.
      if (sceneManager.shadowQuality !== sceneManager.quality.shadowQuality) {
        prefs.shadowQuality = sceneManager.shadowQuality;
      }

      const roomBox = sceneManager.getRoomBox();
      if (roomBox) {
        prefs.roomBox = {
          enabled: roomBox.isEnabled(),
          width: roomBox.width,
          depth: roomBox.depth,
        };
      }
    }

    const dimensionManager = this.app.getDimensionManager();
    if (dimensionManager) {
      prefs.dimensionsEnabled = dimensionManager.isEnabled();
      prefs.dimensionUnit = dimensionManager.getUnit();
    }

    const cameraManager = this.app.getCameraManager();
    if (cameraManager) {
      prefs.panningEnabled = cameraManager.get3DPanningEnabled();
    }

    const eventManager = this.app.getEventManager();
    if (eventManager) {
      prefs.snappingEnabled = eventManager.snappingEnabled;
    }

    // Omitted at the default, like uiStyle — on is what an untouched browser gets.
    if (this.app.getFurnitureManager()?.autoCenter === false) prefs.autoCenter = false;

    // Dev-only overrides, omitted at their default so a production payload never carries them.
    if (SHOW_DEV_TOOLS && this.app.getCollisionSystem()?.collisionEnabled === false) {
      prefs.collisionEnabled = false;
    }
    if (SHOW_DEV_TOOLS && this.app.getFurnitureManager()?.isHighPoly()) {
      prefs.highPoly = true;
    }

    const lastSelectedColor = this.app.getUIManager()?.getLastSelectedColor();
    if (lastSelectedColor) prefs.lastSelectedColor = lastSelectedColor;

    // Omitted at the default, like shadowQuality — layout1 is what an untouched browser gets.
    const uiStyle = this.app.getUIManager()?.uiStyle;
    if (uiStyle && uiStyle !== 'layout1') prefs.uiStyle = uiStyle;

    const localization = this.app.getLocalizationManager();
    if (localization?.hasLocalTranslations()) {
      prefs.languageMode = localization.usingEnglish ? 'en' : 'local';
    }

    return prefs;
  }

  /**
   * Structural + range check; room size lives here now that the room is a preference.
   * @param {*} prefs
   * @returns {boolean}
   */
  validate(prefs) {
    if (!prefs || typeof prefs !== 'object') return false;

    const roomSize = prefs.environment?.roomSize;
    if (roomSize) {
      const { width, depth, height } = roomSize;
      if (!Number.isFinite(width) || width < 1 || width > 50) return false;
      if (!Number.isFinite(depth) || depth < 1 || depth > 50) return false;
      if (height !== undefined && (!Number.isFinite(height) || height < 1 || height > 10)) return false;
    }

    return true;
  }

  /** @returns {Object|null} the stored preferences, or null when absent/unusable. */
  read() {
    try {
      const saved = localStorage.getItem(PREFS_KEY);
      this._stored = null;
      if (!saved) return null;
      const prefs = JSON.parse(saved);
      if (!this.validate(prefs)) {
        console.warn('Invalid user preferences; ignoring');
        return null;
      }
      this._stored = prefs;
      return prefs;
    } catch (error) {
      console.warn('Failed to read user preferences:', error);
      return null;
    }
  }

  /** Persist the current values. Callers go through StateManager.saveSceneState(). */
  save() {
    try {
      localStorage.setItem(PREFS_KEY, JSON.stringify(this.collect()));
      return true;
    } catch (error) {
      if (error.name === 'QuotaExceededError') {
        console.error('localStorage quota exceeded. Cannot save user preferences.');
      } else {
        console.error('Failed to save user preferences:', error);
      }
      return false;
    }
  }

  /**
   * Apply stored preferences at startup, before any layout loads. An absent or unknown stored
   * environment leaves SceneManager.init()'s default in place.
   * @param {Object|null} [prefs] defaults to the stored blob
   * @returns {boolean} whether stored preferences were found
   */
  apply(prefs = this.read()) {
    if (!prefs) return false;

    const sceneManager = this.app.getSceneManager();
    const cameraManager = this.app.getCameraManager();
    const dimensionManager = this.app.getDimensionManager();
    const uiManager = this.app.getUIManager();

    if (prefs.environment && sceneManager) {
      sceneManager.setEnvironmentState(prefs.environment);
      cameraManager?.onEnvironmentChange();

      const envKey = sceneManager.getCurrentEnvironment();
      if (envKey === WHITE_ROOM_ENVIRONMENT) this._restoreCustomFloor(sceneManager);
      uiManager?._updateRoomBoxBtnVisibility(envKey);
    }

    if (dimensionManager) {
      if (prefs.dimensionsEnabled !== undefined) dimensionManager.setEnabled(!!prefs.dimensionsEnabled);
      if (prefs.dimensionUnit) dimensionManager.setUnit(prefs.dimensionUnit);
    }

    // setShadowQuality migrates legacy tiers.
    if (prefs.shadowQuality && sceneManager) sceneManager.setShadowQuality(prefs.shadowQuality);

    if (cameraManager) {
      if (prefs.panningEnabled != null) cameraManager.set3DPanningEnabled(prefs.panningEnabled);
    }

    if (prefs.snappingEnabled != null) {
      const eventManager = this.app.getEventManager();
      if (eventManager) eventManager.snappingEnabled = prefs.snappingEnabled;
    }

    // Only the flag — a restore is not a placement. syncStateToUI(prefs) below syncs the toggle.
    if (prefs.autoCenter != null) this.app.getFurnitureManager()?.setAutoCenter(prefs.autoCenter);

    if (SHOW_DEV_TOOLS && prefs.collisionEnabled === false) {
      const collisionSystem = this.app.getCollisionSystem();
      if (collisionSystem) collisionSystem.collisionEnabled = false;
    }

    // Nothing is loaded yet at this point, so the directory swap alone is enough — no reload.
    if (SHOW_DEV_TOOLS && prefs.highPoly) this.app.getFurnitureManager()?.setHighPoly(true);

    // Seeds the picker for new placements; a preset loaded later is repainted by selectLayout().
    if (prefs.lastSelectedColor) uiManager?.restoreLastSelectedColor(prefs.lastSelectedColor);

    if (prefs.uiStyle) uiManager?.setUIStyle(prefs.uiStyle);

    if (prefs.languageMode) {
      const localization = this.app.getLocalizationManager();
      if (localization?.hasLocalTranslations()) {
        if (prefs.languageMode === 'en') localization.switchToEnglish();
        else localization.switchToLocal();
      }
    }

    // Needed here too for paths that never load a layout (start-from-scratch); the one in
    // loadSceneFromObject() re-runs it to clamp against the furniture.
    this.applyRoomBox(prefs);
    uiManager?.syncStateToUI(prefs);
    return true;
  }

  /**
   * Floor crop only; run after a layout loads, since setSize clamps to the furniture bounds.
   * @param {Object|null} [prefs] defaults to the blob read at apply() time
   */
  applyRoomBox(prefs = this._stored) {
    const roomBox = this.app.getSceneManager()?.getRoomBox();
    if (!roomBox || !prefs?.roomBox) return false;

    roomBox.setSize(prefs.roomBox.width ?? 5, prefs.roomBox.depth ?? 5, this.app.getFurnitureManager());
    if (prefs.roomBox.enabled) roomBox.show();
    else roomBox.hide();
    return true;
  }

  clear() {
    try {
      localStorage.removeItem(PREFS_KEY);
      this._stored = null;
      return true;
    } catch (error) {
      console.error('Failed to clear user preferences:', error);
      return false;
    }
  }

  /** Re-apply the user's uploaded white-room floor, stored separately (it's a data URL). */
  _restoreCustomFloor(sceneManager) {
    const floor = readCustomFloor();
    if (floor?.dataURL) sceneManager.getRoomBuilder()?.applyCustomFloorTexture(floor.dataURL);
  }
}
